import {
  Activity,
  AlertCircle,
  ArrowRight,
  ArrowUp,
  BarChart3,
  Bell,
  Brain,
  BriefcaseBusiness,
  Building2,
  Calendar,
  Camera,
  CheckCircle2,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ChevronUp,
  ClipboardList,
  Code2,
  Coins,
  Columns3,
  Cpu,
  CreditCard,
  DollarSign,
  Eye,
  EyeOff,
  FileText,
  Gauge,
  Gift,
  Github,
  Globe,
  GraduationCap,
  GripVertical,
  Heart,
  Home,
  ImageUp,
  Info,
  Languages,
  Layers,
  LayoutDashboard,
  Lightbulb,
  LineChart,
  Link2,
  Linkedin,
  ListFilter,
  ListChecks,
  LogOut,
  Mail,
  MailCheck,
  MailOpen,
  MapPin,
  Menu,
  Moon,
  Network,
  Pencil,
  Percent,
  Phone,
  PieChart,
  Plus,
  RefreshCw,
  Route,
  RotateCcw,
  Save,
  ScrollText,
  Search,
  Send,
  ServerCrash,
  Settings,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Sun,
  Target,
  Trash2,
  TrendingDown,
  TrendingUp,
  TriangleAlert,
  Twitter,
  UploadCloud,
  UserPlus,
  UserRound,
  Users,
  Wand2,
  X,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/shared/lib/cn';

/**
 * Central icon registry. The whole app references icons by these semantic
 * names via <Icon name="..." />; swapping the underlying glyph is a one-line
 * change here. Backed by lucide-react (the project's chosen icon set — thin,
 * consistent stroke). Names are kebab-case and mostly mirror the lucide names.
 */
const REGISTRY = {
  // actions
  edit: Pencil,
  add: Plus,
  delete: Trash2,
  close: X,
  search: Search,
  save: Save,
  'rotate-ccw': RotateCcw,
  refresh: RefreshCw,
  upload: UploadCloud,
  send: Send,
  filter: ListFilter,
  eye: Eye,
  'eye-off': EyeOff,
  logout: LogOut,
  check: Check,
  // identity / nav
  dashboard: LayoutDashboard,
  gauge: Gauge,
  user: UserRound,
  users: Users,
  'user-plus': UserPlus,
  shield: ShieldCheck,
  settings: Settings,
  sliders: SlidersHorizontal,
  'credit-card': CreditCard,
  brain: Brain,
  briefcase: BriefcaseBusiness,
  building: Building2,
  'clipboard-list': ClipboardList,
  'file-text': FileText,
  audit: ScrollText,
  layers: Layers,
  cpu: Cpu,
  zap: Zap,
  // mail
  mail: Mail,
  'mail-open': MailOpen,
  'mail-check': MailCheck,
  // status
  info: Info,
  'alert-circle': AlertCircle,
  'check-circle': CheckCircle2,
  'triangle-alert': TriangleAlert,
  'shield-alert': ShieldAlert,
  'server-crash': ServerCrash,
  // chrome
  bell: Bell,
  coins: Coins,
  menu: Menu,
  home: Home,
  moon: Moon,
  sun: Sun,
  languages: Languages,
  // chevrons / sort / reorder
  'chevron-right': ChevronRight,
  'chevron-left': ChevronLeft,
  'chevron-up': ChevronUp,
  'chevron-down': ChevronDown,
  'chevrons-up-down': ChevronsUpDown,
  'grip-vertical': GripVertical,
  columns: Columns3,
  // arrows
  'arrow-up': ArrowUp,
  'arrow-right': ArrowRight,
  // charts / marketing
  sparkles: Sparkles,
  wand: Wand2,
  network: Network,
  lightbulb: Lightbulb,
  'bar-chart': BarChart3,
  'line-chart': LineChart,
  'pie-chart': PieChart,
  activity: Activity,
  route: Route,
  'trending-up': TrendingUp,
  'trending-down': TrendingDown,
  dollar: DollarSign,
  percent: Percent,
  image: ImageUp,
  camera: Camera,
  'graduation-cap': GraduationCap,
  'map-pin': MapPin,
  // company / job detail
  globe: Globe,
  link: Link2,
  calendar: Calendar,
  phone: Phone,
  linkedin: Linkedin,
  twitter: Twitter,
  github: Github,
  gift: Gift,
  target: Target,
  heart: Heart,
  code: Code2,
  'list-checks': ListChecks,
} satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof REGISTRY;

export interface IconProps {
  name: IconName;
  /** Pixel size of the glyph (lucide renders square). */
  size?: number;
  className?: string;
  /** Provide for meaningful icons; omit for decorative ones (then aria-hidden). */
  'aria-label'?: string;
  title?: string;
  /** Adds a spin animation (e.g. loading indicators). */
  spin?: boolean;
  style?: React.CSSProperties;
}

/**
 * The single icon component used across the app. Decorative by default
 * (aria-hidden) unless an `aria-label` is supplied.
 */
export function Icon({
  name,
  size = 16,
  className,
  spin,
  style,
  title,
  'aria-label': ariaLabel,
}: IconProps) {
  const Glyph = REGISTRY[name];
  return (
    <Glyph
      size={size}
      className={cn(spin && 'animate-spin', className)}
      style={style}
      aria-hidden={ariaLabel ? undefined : true}
      aria-label={ariaLabel}
      {...(title ? { title } : {})}
    />
  );
}
